import { useEffect, useRef, useState } from "react";

/**
 * PRD 12.3: "Prices visibly animate downward when they change after a
 * purchase. Do not skip this animation, it is the moment that sells the
 * mechanic."
 *
 * Prices mostly fall, but conditional entropy is not monotone, so a clue can
 * genuinely get dearer once the search space narrows (buying `continent` =
 * Oceania makes `coast` briefly cheaper than it is once you know it is
 * Australia). We animate both directions rather than lie about it: down is the
 * headline motion, up is a quieter rise.
 */
export function AnimatedCost({
  value,
  affordable,
}: {
  value: number | null;
  affordable: boolean;
}) {
  const [direction, setDirection] = useState<"" | "falling" | "rising">("");
  const previous = useRef(value);

  useEffect(() => {
    if (previous.current !== null && value !== null && value !== previous.current) {
      setDirection(value < previous.current ? "falling" : "rising");
      const timer = setTimeout(() => setDirection(""), 520);
      previous.current = value;
      return () => clearTimeout(timer);
    }
    previous.current = value;
  }, [value]);

  if (value === null) return <span className="cost unaffordable">—</span>;

  return (
    <span
      key={value}
      className={`cost ${direction} ${affordable ? "" : "unaffordable"}`}
    >
      {value}
    </span>
  );
}

/** Counts toward a new value. Used for the candidate counter. */
export function useDropFlash(value: number) {
  const [flash, setFlash] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    if (value < previous.current) {
      setFlash(true);
      const timer = setTimeout(() => setFlash(false), 460);
      previous.current = value;
      return () => clearTimeout(timer);
    }
    previous.current = value;
  }, [value]);

  return flash;
}
